import { FC, useMemo, useCallback } from 'react'
import * as styles from 'components/features/socialFeed/SocialInsightReview/SocialInsightReview.styl'
import Anchor from 'components/atoms/navigation/Anchor'
import { getFeedCommentAnchorId } from 'helpers/feedHelpers'
import { CommentUid, Comment } from 'interfaces/feed'
import FeedUserInfoContainer from 'components/features/socialFeed/FeedUserInfo/FeedUserInfoContainer'
import { createFeedCommentSelector, memberFeedInfoSelector } from 'selectors/feedSelectors'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { FeedPostText } from 'components/features/socialFeed/FeedPost/FeedPostText'
import AttachmentsBlock from 'components/features/socialFeed/FeedPost/AttachmentsBlock'
import { toggleCommentLikeActionCreator } from 'actions/feedActions'
import { createFeedCommentImagesSelector } from 'selectors/feedImagesSelectors'
import { ImageURLs } from 'interfaces/media'
import { CommentLikeButton } from 'components/features/pfm/SegmentButton/CommentLikeButton'
import { SocialInsightReviewActionsMenu } from 'components/features/socialFeed/SocialInsightReviewActionsMenu/SocialInsightReviewActionsMenu'
import { FeedLinkLoggedOut } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import { useIsPublicPage } from 'helpers/pageHelpers'
import cn from 'classnames'

export type SocialInsightReviewOwnProps = {
  commentUid: CommentUid
}

export const SocialInsightReview: FC<SocialInsightReviewOwnProps> = (props) => {
  const { commentUid } = props

  const commentSelector = useMemo(createFeedCommentSelector, [])
  const comment = useStoreSelector((state) => commentSelector(state, props))

  const member = useStoreSelector(memberFeedInfoSelector)

  const imagesSelector = useMemo(createFeedCommentImagesSelector, [])
  const images = useStoreSelector((state) => imagesSelector(state, props))

  const isOwn = useMemo(() => {
    return Boolean(member && comment && comment.authorUid === member.uid)
  }, [member, comment])

  const isPublic = useIsPublicPage()

  if (!comment) {
    return null
  }

  return (
    <SocialInsightReviewComponent
      comment={ comment }
      images={ images }
      isOwn={ isOwn }
      isPublic={ isPublic }
      commentUid={ commentUid }
    />
  )
}

type SocialInsightReviewComponentProps = {
  comment: Comment
  images: ImageURLs[]
  isOwn: boolean
  isPublic: boolean
  commentUid: CommentUid
}
const SocialInsightReviewComponent: FC<SocialInsightReviewComponentProps> = (props) => {
  const { comment, images, commentUid, isOwn, isPublic } = props

  const nameSection = useMemo(() => (
    <div className={ styles.nameSection }>
      <FeedUserInfoContainer
        userUid={ comment.authorUid }
        created={ comment.created }
        isEdited={ !!comment.modified }
        matchingPeergroup={ comment.peergroup }
        mode='comment'
        isOwn={ isOwn }
        isOpportunityReview
      />
    </div>
  ), [comment, isOwn])

  const actions = useMemo(() => (
    <SocialInsightReviewActionsMenu commentUid={ commentUid } />
  ), [commentUid])

  const dispatch = useStoreDispatch()
  const handleClick = useCallback(() => {
    dispatch(toggleCommentLikeActionCreator.request({ toLike: !comment.is_liked, commentUid, authorUid: comment.authorUid }))
  }, [comment.is_liked, commentUid, dispatch, comment.authorUid])

  const isLikeLoading = useStoreSelector((state) => state.app.feed.likingCommentUids[commentUid])

  const likeButtonChild = useMemo(() => {
    if (isPublic) {
      return (
        <FeedLinkLoggedOut type='offers'>
          <CommentLikeButton counter={ comment.likes_count } />
        </FeedLinkLoggedOut>
      )
    }

    return (
      <CommentLikeButton
        disabled={ isLikeLoading }
        pressed={ comment.is_liked }
        counter={ comment.likes_count }
        onClick={ handleClick }
      />
    )
  }, [comment.is_liked, comment.likes_count, handleClick, isLikeLoading, isPublic])

  const theme = useMemo(() => {
    if (isPublic) {
      return 'outline'
    }
    return 'regular'
  }, [isPublic])

  return (
    <Anchor placement={ getFeedCommentAnchorId(commentUid) }>
      <div className={ cn(styles.socialInsightReview, styles[theme]) }>
        <header className={ styles.header }>
          { nameSection }
          { actions }
        </header>

        <div className={ styles.content }>
          <div className={ styles.text }>
            <FeedPostText
              text={ comment.text }
              mode='comment'
              mentions={ comment.mentions }
            />
          </div>
          <AttachmentsBlock images={ images } links={ comment.links } mode='comment' />
          <div className={ styles.bottomActionsRow }>
            <div className={ styles.likeButtonWrapper }>
              { likeButtonChild }
            </div>
          </div>
        </div>
      </div>

    </Anchor>
  )
}
